/**
 * Application service for making authenticated API calls
 */
import api from './api';
import { API_BASE_URL } from '../utils/constants';

// Always use the normalized API_BASE_URL (already maps app* -> api*)
const getApiBaseUrl = () => API_BASE_URL;

const AppService = {
  /**
   * Make a GET request to the API
   * @param {string} url - The API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with the response data
   */
  get(url, params = {}) {
    // Ensure URL starts with a slash
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    return api.get(normalizedUrl, params)
      .then(response => {
        // Log the response for debugging
        console.log(`GET response for ${url}:`, response);
        // Return the data from the response (which is already the data object from api.get)
        return response;
      })
      .catch(error => {
        console.error(`GET request failed for ${url}:`, error);
        throw error;
      });
  },

  /**
   * Make a POST request to the API
   * @param {string} url - The API endpoint
   * @param {Object} data - The request body
   * @returns {Promise} - Promise with the response data
   */
  post(url, data = {}) {
    // Ensure URL starts with a slash
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    return api.post(normalizedUrl, data)
      .then(response => {
        // Log the response for debugging
        console.log(`POST response for ${url}:`, response);
        // Return the full response data
        return response;
      })
      .catch(error => {
        console.error(`POST request failed for ${url}:`, error);
        throw error;
      });
  },

  /**
   * Make a PUT request to the API
   * @param {string} url - The API endpoint
   * @param {Object} data - The request body
   * @returns {Promise} - Promise with the response data
   */
  put(url, data = {}) {
    // Ensure URL starts with a slash
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    return api.put(normalizedUrl, data)
      .then(response => response.data)
      .catch(error => {
        console.error(`PUT request failed for ${url}:`, error);
        throw error;
      });
  },

  /**
   * Make a DELETE request to the API
   * @param {string} url - The API endpoint
   * @returns {Promise} - Promise with the response data
   */
  delete(url) {
    // Ensure URL starts with a slash
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    return api.delete(normalizedUrl)
      .then(response => response.data)
      .catch(error => {
        console.error(`DELETE request failed for ${url}:`, error);
        throw error;
      });
  },

  /**
   * Get the base API URL
   * @returns {string} - The base API URL
   */
  getBaseUrl() {
    return getApiBaseUrl();
  },
  
  /**
   * Fetch workflows data from the API
   * This fixes the error when fetching workflows in production
   * @returns {Promise} - Promise with workflows data
   */
  fetchWorkflows() {
    console.log(`[DEBUG] Fetching workflow data...`);
    const apiBase = getApiBaseUrl();
    const workflowsUrl = `${apiBase}/api/public/workflows`;
    
    console.log(`[DEBUG] Using endpoint: ${workflowsUrl}`);
    
    // First try with authentication
    return this.get(workflowsUrl)
      .catch(error => {
        console.error('[DEBUG] Error fetching workflows with authentication:', error);
        
        // Try again without authentication as fallback
        console.log(`[DEBUG] Trying public endpoint as fallback`);
        return api.get(workflowsUrl, {}, { headers: { 'Authorization': null } });
      });
  }
};

export default AppService;
